import Close from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import {
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Link,
	Tooltip,
	Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetFamilyMemberFileNoHistoryQuery } from '@/api/family-management/familyApi';
import { FamilyMemberFileNoHistoryDTO } from '@/api/DTO/family/family.interface';
import { FamilyMemberStatusEnum } from '@/api/enum/common.enum';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';

interface Props {
	familyMemberId?: string;
	fileNo?: string | null;
	hasFileNoHistory?: boolean | null;
}

/**
 * Indicator next to Family member file no. that opens every File no. of the
 * person (DMS-REQ-WEB-CHA-004 §七).
 */
export default function FamilyMemberFileNoHistoryButton({
	familyMemberId,
	fileNo,
	hasFileNoHistory,
}: Props) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	const canView = !!familyMemberId && !!hasFileNoHistory;

	const { data, isFetching } = useGetFamilyMemberFileNoHistoryQuery(
		familyMemberId || '',
		{
			skip: !open || !canView,
			refetchOnMountOrArgChange: true,
		}
	);

	if (!canView) return null;

	const rows = data?.data || [];

	const columns: GridColDef<FamilyMemberFileNoHistoryDTO>[] = [
		{
			field: 'familyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 1,
			renderCell: (params) => {
				const { row } = params;
				const label = row.familyMemberFileNo || '-';
				return (
					<Box
						display='flex'
						alignItems='center'
						flexWrap='wrap'
						gap={1}
						paddingY={1}
					>
						{row.clickable && !row.current ? (
							<Link
								href={`/family-management/family-management-list/detail/${row.id}`}
								target='_blank'
								rel='noopener noreferrer'
								variant='body2'
							>
								{label}
							</Link>
						) : (
							<Typography variant='body2'>{label}</Typography>
						)}
						{row.current && (
							<Chip
								label={t('memberDuplication.currentRecord')}
								sx={{
									backgroundColor: 'common.tertiary90',
									color: '#353B02',
								}}
							/>
						)}
					</Box>
				);
			},
		},
		{
			field: 'familyMemberStatusDesc',
			headerName: t('family.status'),
			flex: 1,
			renderCell: (params) =>
				params.row.familyMemberStatusDesc ? (
					<Chip
						color={
							params.row.familyMemberStatus ===
							FamilyMemberStatusEnum.AllServicesClosed
								? 'default'
								: 'chipTertiary'
						}
						label={params.row.familyMemberStatusDesc}
					/>
				) : (
					'-'
				),
		},
	];

	return (
		<>
			<Tooltip title={t('memberDuplication.fileNoHistoryTitle')}>
				<IconButton
					size='small'
					color='secondary'
					aria-label={t('memberDuplication.fileNoHistoryTitle')}
					// Without a File no. the icon lines up with the label.
					sx={{ marginLeft: fileNo ? 0.5 : '-5px' }}
					onClick={() => setOpen(true)}
				>
					<HistoryIcon fontSize='small' />
				</IconButton>
			</Tooltip>
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				maxWidth='md'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('memberDuplication.fileNoHistoryTitle')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => setOpen(false)}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					{isFetching ? (
						<CircularProgress size={24} />
					) : (
						<LocalDataGrid
							rows={rows}
							columns={columns}
							getRowId={(row: FamilyMemberFileNoHistoryDTO) => row.id}
							pagination={false}
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
